import React, { useEffect, useRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement | string,
        params: {
          sitekey: string;
          callback?: (token: string) => void;
          'error-callback'?: (errorCode: string) => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'compact' | 'flexible';
        },
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    grecaptcha?: {
      ready: (callback: () => void) => void;
      render: (
        container: HTMLElement | string,
        params: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark';
        },
      ) => number;
      reset: (opt_widget_id?: number) => void;
    };
  }
}

interface CaptchaProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  className?: string;
  theme?: 'light' | 'dark' | 'auto';
}

/**
 * Componente unificado e resiliente de Captcha.
 * Prioriza Cloudflare Turnstile (100% gratuito, sem limite, sem fricção).
 * Faz fallback elegante para Google reCAPTCHA v2 se apenas a chave do Google estiver configurada.
 * Se nenhuma chave pública estiver configurada no frontend (VITE_TURNSTILE_SITE_KEY ou VITE_RECAPTCHA_SITE_KEY),
 * emite automaticamente um token de bypass seguro para não bloquear testes nem o usuário em dev.
 */
export const Captcha: React.FC<CaptchaProps> = ({
  onVerify,
  onExpire,
  className = '',
  theme = 'light',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | number | null>(null);

  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

  useEffect(() => {
    // Se não há nenhuma chave pública configurada, avisa o callback com token de teste
    if (!turnstileSiteKey && !recaptchaSiteKey) {
      onVerify('TEST_CAPTCHA_TOKEN_PASSED');
      return;
    }

    let isMounted = true;

    // 1. Prioridade: Cloudflare Turnstile
    if (turnstileSiteKey) {
      const renderTurnstile = () => {
        if (!containerRef.current || !window.turnstile || !isMounted) return;
        try {
          if (widgetIdRef.current !== null && typeof widgetIdRef.current === 'string') {
            window.turnstile.remove(widgetIdRef.current);
          }
          const id = window.turnstile.render(containerRef.current, {
            sitekey: turnstileSiteKey,
            callback: (token: string) => {
              if (isMounted) onVerify(token);
            },
            'expired-callback': () => {
              if (isMounted && onExpire) onExpire();
            },
            theme,
          });
          widgetIdRef.current = id;
        } catch (e) {
          console.error('[Captcha] Erro ao renderizar Turnstile:', e);
        }
      };

      if (!window.turnstile) {
        const scriptId = 'cloudflare-turnstile-script';
        if (!document.getElementById(scriptId)) {
          const script = document.createElement('script');
          script.id = scriptId;
          script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
          script.async = true;
          script.defer = true;
          script.onload = () => renderTurnstile();
          document.head.appendChild(script);
        } else {
          const checkInterval = setInterval(() => {
            if (window.turnstile) {
              clearInterval(checkInterval);
              renderTurnstile();
            }
          }, 100);
          return () => clearInterval(checkInterval);
        }
      } else {
        renderTurnstile();
      }

      return () => {
        isMounted = false;
        if (widgetIdRef.current !== null && typeof widgetIdRef.current === 'string' && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      };
    }

    // 2. Fallback: Google reCAPTCHA
    if (recaptchaSiteKey) {
      const renderRecaptcha = () => {
        if (!containerRef.current || !window.grecaptcha || !isMounted) return;
        window.grecaptcha.ready(() => {
          if (!containerRef.current || !isMounted) return;
          try {
            const id = window.grecaptcha!.render(containerRef.current, {
              sitekey: recaptchaSiteKey,
              callback: (token: string) => {
                if (isMounted) onVerify(token);
              },
              'expired-callback': () => {
                if (isMounted && onExpire) onExpire();
              },
              theme: theme === 'auto' ? 'light' : theme,
            });
            widgetIdRef.current = id;
          } catch (e) {
            console.error('[Captcha] Erro ao renderizar reCAPTCHA:', e);
          }
        });
      };

      if (!window.grecaptcha) {
        const scriptId = 'google-recaptcha-script';
        if (!document.getElementById(scriptId)) {
          const script = document.createElement('script');
          script.id = scriptId;
          script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
          script.async = true;
          script.defer = true;
          script.onload = () => renderRecaptcha();
          document.head.appendChild(script);
        }
      } else {
        renderRecaptcha();
      }

      return () => {
        isMounted = false;
      };
    }
    return () => {
      isMounted = false;
    };
  }, [turnstileSiteKey, recaptchaSiteKey, theme]);

  if (!turnstileSiteKey && !recaptchaSiteKey) {
    return null;
  }

  return (
    <div className={`medikei-captcha-container my-3 flex justify-center ${className}`}>
      <div ref={containerRef} />
    </div>
  );
};
